import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAgGridLocale } from "../../../../../config/globalConfig";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, StatusPanelDef } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../constants/PaymentsRecoil";
import { usePaymentRepository } from "../../../api/repositories/PaymentRepository";
import { EditAttributesRounded } from "@mui/icons-material";
import { formatCurrency, formatDate } from "../../../utils/FormatUtils";
import { Box } from "@mui/system";
import { Tooltip } from "@mui/material";
import ColouredChip from "../../../component/ColouredChip";
import { COLOR_OK } from "../constants/PaymentsColors";
import GridStatusComponent from "../../../experiments/GridStatusComponent";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";

const PaymentsTable = (props: any) => {

    const UNIQUE_ID = "fnn-payments";

    const {t} = useTranslation('finance', {keyPrefix: 'payments'});
    const {getPayments} = usePaymentRepository();

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);

    const gridRef = useRef<AgGridReact>(null);
    const agGridLocale = useAgGridLocale();

    const columnDefs : ColDef[] = [
        {
            field: 'id',
            headerName: t('tables.payment.txn-id'),
            headerTooltip: t('tables.payment.txn-id'),
            minWidth: 100,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'posTransactionId',
            headerName: t('tables.payment.txn-code'),
            headerTooltip: t('tables.payment.txn-code'),
            minWidth: 200,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'customer',
            headerName: t('tables.payment.customer'),
            // headerTooltip: t('tables.payment.type'),
            minWidth: 150,
            unSortIcon: false,
            sortable: false,
        },
        {
            field: 'rentalAgreement',
            headerName: t('tables.payment.ra'),
            // headerTooltip: t('tables.payment.type'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'actualPaymentDate',
            headerName: t('tables.payment.accounted-amount-date'),
            // headerTooltip: t('tables.payment.type'),
            minWidth: 200,
            unSortIcon: true,
            cellRenderer: (params: any) => formatDate(params.value)
        },


        {
            field: 'source',
            headerName: t('tables.payment.type'),
            headerTooltip: t('tables.payment.type'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(`filters.payments.types.${params.value}`)
        },
        {
            field: 'mode',
            headerName: t('tables.payment.mode'),
            headerTooltip: t('tables.payment.mode'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(`filters.payments.modes.${params.value}`)
        },
        {
            field: 'preAuthorizedPayment',
            headerName: t('tables.payment.pre-authorized-amount'),
            headerTooltip: t('tables.payment.pre-authorized-amount'),
            minWidth: 250,
            unSortIcon: true,
            cellRendererSelector: params => {
                let modified = params.data.amountModified;
                return {
                    component: () => {
                        if (!!modified) {
                            return (
                                <Box sx={{display: 'flex', alignItems: 'center', height: '100%'}}>
                                    {formatCurrency(params.data.preAuthorizedPayment, 'EUR', 0.01)}
                                    <Tooltip title={t('tables.payment.amount-modified')} arrow>
                                        <EditAttributesRounded color={'primary'} sx={{marginLeft: '5px'}}/>
                                    </Tooltip>
                                </Box>
                            );
                        } else {
                            return <>{formatCurrency(params.data.preAuthorizedPayment, 'EUR', 0.01)}</>
                        }
                    }
                }
            }
        },
        {
            field: 'actualPayment',
            headerName: t('tables.payment.accounted-amount'),
            headerTooltip: t('tables.payment.accounted-amount'),
            minWidth: 250,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: 'created',
            headerName: t('tables.payment.created-date'),
            headerTooltip: t('tables.payment.created-date'),
            minWidth: 200,
            unSortIcon: true,
            cellRenderer: (params: any) => formatDate(params.value)
        },
        {
            field: 'modified',
            headerName: t('tables.payment.modified-date'),
            headerTooltip: t('tables.payment.modified-date'),
            minWidth: 200,
            unSortIcon: true,
            cellRenderer: (params: any) => formatDate(params.value)
        },
        {
            field: 'status',
            headerName: t('tables.payment.status'),
            headerTooltip: t('tables.payment.status'),
            minWidth: 100,
            sortable: false,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => <ColouredChip name={'OK'} color={COLOR_OK}/>
                };
            },
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating])


    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getPayments(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {
                        let result = response.result;

                        if (result.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.totalElements
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }

    const statusBar = useMemo<{ statusPanels: StatusPanelDef[] }>(() => {
        return {
            statusPanels: [
                {
                    statusPanel: GridStatusComponent
                }
            ],
        };
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    return (
        <div className="ag-theme-alpine">
            <AgGridReact domLayout='autoHeight'
                         rowModelType='serverSide'
                         pagination paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                         ref={gridRef} columnDefs={columnDefs} gridOptions={gridOptions} localeText={agGridLocale}
                         rowSelection={'multiple'}
                         statusBar={statusBar}
                         suppressRowClickSelection
                         onGridReady={() => {
                             gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                             let gridState = getLocalGridState(UNIQUE_ID).cols;
                             if (gridState !== undefined) {
                                 gridRef.current!.columnApi.applyColumnState({
                                     state: gridState,
                                     applyOrder: true
                                 })
                             }
                         }}
                         onColumnMoved={e => {
                             if (e.source === "api") return;
                             saveState();
                         }}
                         onColumnPinned={e => {
                             if (e.source === "api") return;
                             saveState();
                         }}
                         onColumnResized={e => {
                             if (e.source === "api") return;
                             saveState();
                         }}
                         onSortChanged={e => {
                             if (e.source === "api") return;
                             saveState();
                         }}
                         onPaginationChanged={() => {
                             setLocalGridState(UNIQUE_ID, {
                                 rows: gridRef.current!.api?.paginationGetPageSize(),
                                 cols: getLocalGridState(UNIQUE_ID).cols
                             });
                         }}
                         onGridSizeChanged={() => gridRef.current!.api.sizeColumnsToFit()}
            >
            </AgGridReact>
        </div>
    );
}

export default PaymentsTable;